import React, { useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import { t } from 'i18next';

import POSHeading from '@/components/POS/Common/POSHeading';
import POSButton from '@/components/POS/Common/POSButton';
import POSDeleteModal from '@/components/POS/Common/POSDeleteModal';
import Permission, { PermissionDenied } from '@/utils/POS/Permission';

import { useCreateUnit, useDeleteUnit, useGetUnits, useUpdateUnit } from '@/hooks/api/pos/units/useUnits';
import type { GetApiUnits200Item } from '@/shared/api/models';

import ProductUnitsList from './List/ProductUnitsList';
import UnitCreateEditModal from './Modals/UnitCreateEditModal';

type UnitFormValues = {
    name: string;
    abbreviation: string;
};

export default function ProductUnits() {
    const { isAllowed } = Permission();

    const canRead = isAllowed('Unit', 'read');
    const canCreate = isAllowed('Unit', 'create');
    const canUpdate = isAllowed('Unit', 'update');
    const canDelete = isAllowed('Unit', 'delete');

    const { data: units, isLoading } = useGetUnits();

    const createUnit = useCreateUnit();
    const updateUnit = useUpdateUnit();
    const deleteUnit = useDeleteUnit();

    const [unitModalOpen, setUnitModalOpen] = useState(false);
    const [unitToEdit, setUnitToEdit] = useState<GetApiUnits200Item | null>(null);

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [unitToDelete, setUnitToDelete] = useState<GetApiUnits200Item | null>(null);

    const createOrUpdate = useMemo(() => {
        return async (values: UnitFormValues) => {
            if (unitToEdit?.id) {
                if (unitToEdit.isDefault === true) return;
                await updateUnit.mutateAsync({
                    id: unitToEdit.id,
                    body: values,
                });
                return;
            }

            await createUnit.mutateAsync(values);
        };
        // Intentionally depend on unitToEdit id to keep correct behavior across modal open/close.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitToEdit?.id, createUnit, updateUnit]);

    const openCreateModal = () => {
        setUnitToEdit(null);
        setUnitModalOpen(true);
    };

    const openEditModal = (unit: GetApiUnits200Item) => {
        if (!canUpdate || unit.isDefault === true) return;
        setUnitToEdit(unit);
        setUnitModalOpen(true);
    };

    const openDeleteModal = (unit: GetApiUnits200Item) => {
        if (!canDelete || unit.isDefault === true) return;
        setUnitToDelete(unit);
        setDeleteModalOpen(true);
    };

    if (!canRead) {
        return (
            <Stack>
                <PermissionDenied />
            </Stack>
        );
    }

    return (
        <Stack sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Stack
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: { md: 'space-between' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    pb: 2,
                    width: '100%',
                }}
            >
                <POSHeading sx={{ my: 2 }} text={t('POS.ProductUnits')} />
                {canCreate && (
                    <POSButton
                        title={`+ ${t('POS.CreateUnit')}`}
                        variant="save"
                        width={{ xs: '100%', md: 'auto' }}
                        onClick={openCreateModal}
                    />
                )}
            </Stack>

            <ProductUnitsList
                units={units ?? []}
                loading={isLoading}
                onEdit={openEditModal}
                onDelete={openDeleteModal}
                onRowClick={canUpdate ? openEditModal : undefined}
            />

            {deleteModalOpen && unitToDelete && (
                <POSDeleteModal
                    open={deleteModalOpen}
                    handleClose={() => {
                        setDeleteModalOpen(false);
                        setUnitToDelete(null);
                    }}
                    title={t('POS.RemoveUnit')}
                    description={t('POS.RemoveUnitDesc')}
                    onClickDismiss={() => {
                        setDeleteModalOpen(false);
                        setUnitToDelete(null);
                    }}
                    disabled={deleteUnit.isPending}
                    onClickConfirm={async () => {
                        if (!unitToDelete || unitToDelete.isDefault === true) return;
                        try {
                            await deleteUnit.mutateAsync(unitToDelete.id);
                            setDeleteModalOpen(false);
                            setUnitToDelete(null);
                        } catch (err) {
                            // The hook already shows the backend error toast.
                            // Keep the modal open so the user can try again.
                            console.error('[ProductUnits] delete failed:', err);
                        }
                    }}
                />
            )}

            <UnitCreateEditModal
                open={unitModalOpen}
                onClose={() => {
                    setUnitModalOpen(false);
                    setUnitToEdit(null);
                }}
                unit={unitToEdit}
                onSubmit={createOrUpdate}
            />
        </Stack>
    );
}

